import { useEffect } from 'react';

/**
 * SEO — Dynamic head-tag + JSON-LD injector for React SPA pages.
 * Modern LLM crawlers (GPTBot, ClaudeBot, PerplexityBot, Google-Extended) render JS,
 * so per-route dynamic tags are indexed correctly.
 *
 * Usage:
 *   <SEO
 *     title="..."
 *     description="..."
 *     canonical="https://crilamdecorative.com/route"
 *     jsonLd={[{...schema}, {...faqPage}]}
 *   />
 */
const setMeta = (name, content, isProperty = false) => {
  if (!content) return;
  const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    if (isProperty) el.setAttribute('property', name); else el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href, extras = {}) => {
  if (!href) return;
  const selector = `link[rel="${rel}"]${extras.hreflang ? `[hreflang="${extras.hreflang}"]` : ''}`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (extras.hreflang) el.setAttribute('hreflang', extras.hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const SEO = ({
  title,
  description,
  canonical,
  image = 'https://crilamdecorative.com/logo512.png',
  keywords,
  jsonLd = [],
  hreflangs = [],
  articlePublished,
  articleModified,
  ogType = 'website',
}) => {
  useEffect(() => {
    if (title) document.title = title;
    setMeta('title', title);
    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    // Open Graph
    setMeta('og:type', ogType, true);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:image', image, true);
    if (canonical) setMeta('og:url', canonical, true);

    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);

    // Canonical
    if (canonical) setLink('canonical', canonical);

    // hreflang variants
    hreflangs.forEach(({ lang, href }) => setLink('alternate', href, { hreflang: lang }));

    // Article-specific meta
    if (articlePublished) setMeta('article:published_time', articlePublished, true);
    if (articleModified) setMeta('article:modified_time', articleModified, true);

    // JSON-LD scripts — remove any previously injected page-specific schemas first
    const oldScripts = document.head.querySelectorAll('script[data-seo="page"]');
    oldScripts.forEach((s) => s.remove());
    (jsonLd || []).forEach((schema) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo', 'page');
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
    });

    return () => {
      document.head.querySelectorAll('script[data-seo="page"]').forEach((s) => s.remove());
    };
  }, [title, description, canonical, image, keywords, jsonLd, hreflangs, articlePublished, articleModified, ogType]);

  return null;
};

// ---------- Reusable schema builders ----------

export const buildFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: (faqs || []).map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

export const buildArticleSchema = ({ headline, description, url, image, datePublished, dateModified, author = 'CriLam Decorative' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline,
  description,
  image: image || 'https://crilamdecorative.com/logo512.png',
  datePublished,
  dateModified: dateModified || datePublished,
  author: { '@type': 'Organization', name: author, url: 'https://crilamdecorative.com/' },
  publisher: {
    '@type': 'Organization',
    name: 'CriLam Decorative Pvt. Ltd.',
    logo: { '@type': 'ImageObject', url: 'https://crilamdecorative.com/logo512.png' },
  },
  mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  url,
});

export const buildBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

export const buildProductSchema = ({ name, description, image, brand = 'CriLam', category, url }) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  image: image || 'https://crilamdecorative.com/logo512.png',
  brand: { '@type': 'Brand', name: brand },
  category,
  url,
  manufacturer: { '@type': 'Organization', name: 'CriLam Decorative Pvt. Ltd.', url: 'https://crilamdecorative.com/' },
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'INR',
    lowPrice: '1500',
    highPrice: '25000',
    availability: 'https://schema.org/InStock',
    seller: { '@type': 'Organization', name: 'CriLam Decorative Pvt. Ltd.' },
  },
});

export const buildLocalBusinessSchema = ({ city, region = 'Uttar Pradesh' }) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: `CriLam Decorative — ${city} Service Area`,
  image: 'https://crilamdecorative.com/logo512.png',
  telephone: '+91-8298297475',
  email: 'info@crilamdecorative.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '123/628 (E) Domill Street, Fazalganj',
    addressLocality: 'Kanpur',
    addressRegion: region,
    postalCode: '208012',
    addressCountry: 'IN',
  },
  areaServed: { '@type': 'City', name: city, containedInPlace: { '@type': 'AdministrativeArea', name: region } },
  url: `https://crilamdecorative.com/acrylic-boards-${city.toLowerCase().replace(/\s+/g, '-')}`,
});

export default SEO;
